import React from 'react';
import { Row, Col, FormGroup, Input, InputGroup, InputGroupAddon, InputGroupText, Table } from 'reactstrap';
import { PaymentInfoSectionProps, PaymentField } from '../interfaces/payment.interface';

// ============ TYPES ============
interface PaymentDetailRow {
  index: number;
  description: string;
  percentage: string;
  amount: string;
  payment_type: string;
  date?: string;
}

interface FinanceState {
  paymentTypeReserva: string;
  paymentTypeFirma: string;
  paymentTypeComplemento: string;
  paymentTypeEscrituracion: string;
}

// ============ CONSTANTS ============
const INITIAL_STATE: FinanceState = {
  // ACTUALIZADO: Valores por defecto como texto
  paymentTypeReserva: 'único pago',
  paymentTypeFirma: 'único pago',
  paymentTypeComplemento: 'único pago',
  paymentTypeEscrituracion: 'único pago',
  };

// ============ HELPER FUNCTIONS ============
const formatDate = (date: Date | null): string => {
  if (!date) return '';
  
  const months = [
    'ene', 'feb', 'mar', 'abr', 'may', 'jun',
    'jul', 'ago', 'sep', 'oct', 'nov', 'dic'
  ];
  
  const month = months[date.getMonth()];
  const year = date.getFullYear().toString().slice(-2);
  
  return `${month}-${year}`;
};

const formatCurrency = (amount: number, symbol: string): string => {
  return `${symbol} ${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatPercentage = (percentage: number): string => {
  return `${percentage.toFixed(2)}%`;
};

const formatPaymentType = (paymentType: string, amount: number, symbol: string): string => {
  if (!paymentType || paymentType === '' || paymentType === 'unico_pago') {
    return 'único pago';
  }
  
  // Si el usuario escribió algo personalizado, usarlo directamente
  return paymentType;
};

// EXPORTAR la función para uso en createQuotationPayload
export const generatePaymentDetailTable = (
  financeState: any,
  symbolData: string,
  paymentFields: PaymentField[]
) => {
  const PAYMENT_FIELDS = paymentFields && paymentFields.length > 0 ? paymentFields : [];

  const paymentDetailTable = [];

  PAYMENT_FIELDS.forEach((field, index) => {
    const percentage = (financeState as any)[field.percentageKey] || 0;
    const amount = (financeState as any)[field.amountKey] || 0;
    const date = financeState[field.dateKey as keyof typeof financeState] as Date | null;
    const paymentType = field.paymentTypeKey ? (financeState as any)[field.paymentTypeKey] || 'único pago' : 'único pago';

    paymentDetailTable.push({
      index,
      description: field.label,
      percentage: `${percentage.toFixed(2)}%`,
      amount: Math.round(amount * 100) / 100,
      payment_type: paymentType,
      date: formatDate(date)
    });
  });

  paymentDetailTable.push({
    index: PAYMENT_FIELDS.length,
    description: "Total",
    percentage: `${financeState.totalPercentage.toFixed(2)}%`,
    amount: Math.round(financeState.totalAmount * 100) / 100,
    payment_type: "",
    date: ""
  });

  return paymentDetailTable;
};

// ============ SHARED COMPONENTS ============
interface FieldProps {
  label: string;
  type?: string;
  placeholder: string;
  value: string | number;
  onChange: (value: string) => void;
  readOnly?: boolean;
  colSize?: string;
  symbolData?: string;
}

const Field: React.FC<FieldProps> = ({ 
  label, 
  type = 'text', 
  placeholder, 
  value, 
  onChange, 
  readOnly = false,
  colSize = '4',
  symbolData
}) => {
  const displayValue = (() => {
    if (type === 'number' && (value === 0 || value === '0')) {
      return '';
    }
    return value;
  })();
  
  const hasContent = (() => {
    if (type === 'number') {
      return value !== '' && value !== 0 && value !== '0' && value !== null && value !== undefined;
    }
    return value !== '' && value !== null && value !== undefined;
  })();
  
  const inputStyles = {
    ...(readOnly ? { backgroundColor: '#f8f9fa' } : {}),
    ...(hasContent ? { color: '#000000', fontWeight: '500' } : {})
  };

  return (
    <Col md={colSize} className="mb-3">
      <FormGroup>
        <label className="form-control-label">{label}</label>
        {symbolData ? (
          <InputGroup>
            <InputGroupAddon addonType="prepend">
              <InputGroupText>{symbolData}</InputGroupText>
            </InputGroupAddon>
            <Input
              type={type as any}
              placeholder={placeholder}
              value={displayValue}
              onChange={(e) => onChange(e.target.value)}
              readOnly={readOnly}
              style={inputStyles}
            />
          </InputGroup>
        ) : (
          <Input
            type={type as any}
            placeholder={placeholder}
            value={displayValue}
            onChange={(e) => onChange(e.target.value)}
            readOnly={readOnly}
            style={inputStyles}
          />
        )}
      </FormGroup>
    </Col>
  );
};

interface SectionProps {
  title: string;
  borderColor: string;
  children: React.ReactNode;
  backgroundColor?: string;
}

const Section: React.FC<SectionProps> = ({ 
  title, 
  borderColor, 
  children, 
  backgroundColor = '#f8f9fa' 
}) => (
  <div style={{
    border: '1px solid #dee2e6',
    borderRadius: '8px',
    padding: '20px',
    marginTop: '20px',
    backgroundColor
  }}>
    <h5 style={{
      marginBottom: '20px',
      color: '#495057',
      borderBottom: `2px solid ${borderColor}`,
      paddingBottom: '8px'
    }}>
      {title}
    </h5>
    {children}
  </div>
);

 // ============ MAIN COMPONENT ============
 const PaymentInformationQuotationComponent: React.FC<PaymentInfoSectionProps & { paymentFields?: PaymentField[] }> = ({
   financeState,
   symbolData,
   onInputChange,
   onDateChange,
   onPaymentTypeChange,
   paymentFields,
 }) => {
  // Usa los campos recibidos o un array vacío por defecto
  const PAYMENT_FIELDS: PaymentField[] = paymentFields && paymentFields.length > 0
    ? paymentFields
    : [];

  const totalUsed = financeState.totalAmount;
  const remainingAmount = Math.max(0, financeState.modelUnitBasePrice - totalUsed);
  const isOverLimit = totalUsed > financeState.modelUnitBasePrice;

  const paymentDetailTable = generatePaymentDetailTable(financeState, symbolData, PAYMENT_FIELDS);

  return (
    <Section title="Información de Pagos" borderColor="#007bff" backgroundColor="#fff">
      {/* Mostrar información del límite */}
      <Row className="mb-3">
        <Col md="12">
          <div style={{
            padding: '10px',
            borderRadius: '4px',
            backgroundColor: isOverLimit ? '#f8d7da' : '#d1edff',
            border: `1px solid ${isOverLimit ? '#f5c6cb' : '#bee5eb'}`,
            color: isOverLimit ? '#721c24' : '#0c5460'
          }}>
            <strong>Precio base:</strong> {formatCurrency(financeState.modelUnitBasePrice, symbolData)} | 
            <strong> Total usado:</strong> {formatCurrency(totalUsed, symbolData)} | 
            <strong> Disponible:</strong> {formatCurrency(remainingAmount, symbolData)}
            {isOverLimit && <div><strong>⚠️ El total excede el precio base</strong></div>}
          </div>
        </Col>
      </Row>

      {/* Formularios de entrada */}
      <Row className="mb-3">
        <Col md="12">
          <h6 style={{ color: '#6c757d', marginBottom: '15px', borderBottom: '1px solid #dee2e6', paddingBottom: '5px' }}>
            Configuración de Pagos
          </h6>
        </Col>
      </Row>

      {PAYMENT_FIELDS.map((field, index) => {
        const rawPercentage = (financeState as any)[field.percentageKey];
        const percentageValue = (typeof rawPercentage === 'number' || typeof rawPercentage === 'string') ? rawPercentage : '';
        const rawAmount = (financeState as any)[field.amountKey];
        const amountValue = typeof rawAmount === 'number' ? rawAmount : 0;
        const dateValue = field.dateKey ? (financeState as any)[field.dateKey] as Date | null : null;
        const paymentType = field.paymentTypeKey ? (financeState as any)[field.paymentTypeKey] || 'único pago' : 'único pago';
        
        const hasDateContent = dateValue !== null;
        const hasPaymentTypeContent = paymentType && paymentType !== '' && paymentType !== 'único pago';
        
        return (
          <Row key={field.key} className="mb-3" style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '8px', border: '1px solid #dee2e6' }}>
            {/* ÍNDICE */}
            <Col md="1" className="d-flex align-items-center justify-content-center">
              <div style={{
                width: '30px',
                height: '30px',
                borderRadius: '50%',
                backgroundColor: '#007bff',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 'bold',
                fontSize: '14px'
              }}>
                {index + 1}
              </div>
            </Col>
            
            {/* CAMPOS */}
            <Col md="11">
              <Row>
                <Field
                  label={`${field.label} (%)`}
                  type="number"
                  placeholder="0"
                  value={typeof percentageValue === 'number' ? percentageValue.toFixed(2) : parseFloat(percentageValue.toString()).toFixed(2)}
                  onChange={(v) => onInputChange(field.percentageKey, v)}
                  colSize="3"
                />
                <Field
                  label={`${field.label} (Monto)`}
                  type="number"
                  placeholder="0"
                  value={amountValue}
                  onChange={(v) => onInputChange(field.amountKey, v)}
                  symbolData={symbolData}
                  colSize="3"
                />
                {/* MODIFICADO: TIPO DE PAGO COMO INPUT TEXT */}
                <Col md="3">
                  <FormGroup>
                    <label className="form-control-label">Tipo de Pago</label>
                    <Input
                      type="text"
                      placeholder="único pago"
                      value={paymentType}
                      onChange={(e) => {
                        if (field.paymentTypeKey && onPaymentTypeChange) {
                          onPaymentTypeChange(field.paymentTypeKey, e.target.value);
                        }
                      }}
                      style={{
                        ...(hasPaymentTypeContent ? { color: '#000000', fontWeight: '500' } : {})
                      }}
                    />
                  </FormGroup>
                </Col>
                <Col md="3">
                  <FormGroup>
                    <label className="form-control-label">Fecha</label>
                    <Input
                      type="date"
                      value={dateValue ? dateValue.toISOString().slice(0, 10) : ''}
                      onChange={(e) => {
                        if (field.dateKey && e.target.value) {
                          onDateChange(field.dateKey, new Date(e.target.value));
                        }
                      }}
                      style={{
                        ...(hasDateContent ? { color: '#000000', fontWeight: '500' } : {})
                      }}
                    />
                  </FormGroup>
                </Col>
              </Row>
            </Col>
          </Row>
        );
      })}

      {/* FILA TOTAL */}
      <Row className="mt-3" style={{
        backgroundColor: '#e9ecef',
        padding: '15px',
        borderRadius: '8px',
        border: '2px solid #007bff'
      }}>
        <Col md="1" className="d-flex align-items-center justify-content-center">
          <div style={{
            width: '30px',
            height: '30px',
            borderRadius: '50%',
            backgroundColor: '#28a745',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 'bold',
            fontSize: '14px'
          }}>
            T
          </div>
        </Col>
        
        <Col md="11">
          <Row>
            <Field
              label="Total %"
              type="number"
              placeholder=""
              value={(financeState.totalPercentage).toFixed(2)}
              onChange={() => {}}
              readOnly
              colSize="4"
            />
            <Field
              label="Total Monto"
              type="number"
              placeholder=""
              value={Math.round(financeState.totalAmount)}
              onChange={() => {}}
              readOnly
              symbolData={symbolData}
              colSize="4"
            />
            <Col md="4" className="d-flex align-items-center justify-content-center">
              <div style={{ 
                fontSize: '16px', 
                fontWeight: 'bold', 
                color: '#007bff',
                textAlign: 'center',
                marginTop: '25px'
              }}>
                💰 TOTAL GENERAL
              </div>
            </Col>
          </Row>
        </Col>
      </Row>
    </Section>
  );
};

export default PaymentInformationQuotationComponent;